import React from "react";
import useDispatchPlanningLiveSync from "./useDispatchPlanningLiveSync.js";
import { readDispatchDateFromUrl, readDispatchPeriodFromUrl } from "../utils/dispatchDateUrls.js";
import { fetchWithSupabaseAuth } from "../lib/supabaseClient.js";
import { subscribePlanningStateChange } from "../utils/planningCrossViewSync.js";
import {
  buildEveningGanttRailUrl,
  isUsableEveningGanttRailPayload,
  shouldRefreshEveningGanttRail,
  scopeEveningGanttRailPayload,
  mergeEveningSuspendedUpdate,
} from "../utils/eveningGanttRail.js";

function normalizeErrorMessage(payload = null, fallbackMessage = "") {
  const candidates = [
    payload?.error,
    typeof payload?.detail === "string" ? payload.detail : "",
    payload?.detail?.error,
    payload?.detail?.message,
    payload?.message,
  ];
  return candidates.find((value) => typeof value === "string" && value.trim())?.trim()
    || fallbackMessage;
}

export function buildEveningGanttRailErrorState(
  currentState = { payload: null },
  error = null,
) {
  return {
    ...currentState,
    errorMessage: error instanceof Error && error.message
      ? error.message
      : "Impossible de charger le dispatch Soir.",
    isLoading: false,
  };
}

export default function useEveningGanttRail({ dispatchDate = "", enabled = false } = {}) {
  const [requestVersion, setRequestVersion] = React.useState(0);
  const activeRequestRef = React.useRef(null);
  const [state, setState] = React.useState({
    dispatchDate: "",
    errorMessage: "",
    isLoading: false,
    payload: null,
  });

  const retry = React.useCallback(() => {
    setRequestVersion((currentVersion) => currentVersion + 1);
  }, []);

  // BroadcastChannel only reaches tabs in this browser. The compact server
  // revision probe also detects changes made by another user's session.
  useDispatchPlanningLiveSync({
    currentResultsData: state.dispatchDate === dispatchDate ? state.payload : null,
    dispatchDate,
    dispatchPeriod: "evening",
    enabled: enabled && Boolean(dispatchDate),
    revalidator: { state: state.isLoading ? "loading" : "idle", revalidate: retry },
  });

  React.useEffect(() => {
    if (!enabled || !dispatchDate) {
      setState({ dispatchDate: "", errorMessage: "", isLoading: false, payload: null });
      return undefined;
    }

    const abortController = new AbortController();
    activeRequestRef.current = abortController;
    setState((currentState) => ({
      ...currentState,
      dispatchDate,
      payload: currentState.dispatchDate === dispatchDate ? currentState.payload : null,
      errorMessage: "",
      isLoading: true,
    }));

    fetchWithSupabaseAuth(buildEveningGanttRailUrl(dispatchDate), {
      signal: abortController.signal,
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || (payload?.error && !isUsableEveningGanttRailPayload(payload))) {
          throw new Error(normalizeErrorMessage(
            payload,
            response.status === 404
              ? "Aucun dispatch Soir optimisé n'est disponible pour cette date."
              : "Impossible de charger le dispatch Soir.",
          ));
        }
        return scopeEveningGanttRailPayload(payload, dispatchDate);
      })
      .then((payload) => {
        if (abortController.signal.aborted) {
          return;
        }
        setState({ dispatchDate, errorMessage: "", isLoading: false, payload });
      })
      .catch((error) => {
        if (abortController.signal.aborted) {
          return;
        }
        setState((currentState) => buildEveningGanttRailErrorState(currentState, error));
      });

    return () => abortController.abort();
  }, [dispatchDate, enabled, requestVersion]);

  React.useEffect(() => {
    if (!enabled || !dispatchDate) {
      return undefined;
    }

    return subscribePlanningStateChange((detail) => {
      if (shouldRefreshEveningGanttRail(detail, dispatchDate)) {
        const update = detail?.fromBroadcast !== true ? detail?.eveningSuspendedUpdate : null;
        if (mergeEveningSuspendedUpdate(null, update, dispatchDate)) {
          // A GET started before the commit must not replace this saved view.
          activeRequestRef.current?.abort();
          setState((currentState) => ({
            dispatchDate, errorMessage: "", isLoading: false,
            payload: mergeEveningSuspendedUpdate(
              currentState.dispatchDate === dispatchDate ? currentState.payload : null,
              update, dispatchDate,
            ),
          }));
          return;
        }
        retry();
      }
    });
  }, [dispatchDate, enabled, retry]);

  React.useEffect(() => {
    if (!enabled || !dispatchDate) return undefined;
    const onCommitted = (event) => {
      const resultUrl = event.detail?.resultUrl || "";
      if (readDispatchDateFromUrl(resultUrl) === dispatchDate && readDispatchPeriodFromUrl(resultUrl) === "evening") {
        retry();
      }
    };
    window.addEventListener("grain:optimization-results-committed", onCommitted);
    return () => window.removeEventListener("grain:optimization-results-committed", onCommitted);
  }, [dispatchDate, enabled, retry]);

  return {
    ...(enabled && state.dispatchDate === dispatchDate
      ? state
      : { dispatchDate, errorMessage: "", isLoading: enabled, payload: null }),
    retry,
  };
}
